import React from 'react';
import { ExternalLink } from 'lucide-react';
import { SUMMIT_CONFIG } from '../config/summitConfig';

interface FooterProps {
  reducedMotion: boolean;
}

export const Footer: React.FC<FooterProps> = ({ reducedMotion }) => {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <footer className="relative bg-[#0a0a0a] text-ivory border-t border-bronze/30 pt-20 pb-12 overflow-hidden">
      
      {/* Top Greek Key Band */}
      <div className="w-full h-3 greek-key-border opacity-40 mb-12" />

      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32">
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          
          {/* Column 1: Brand & Tagline */}
          <div className="md:col-span-5 space-y-4">
            <div className="w-16 h-16 mb-2">
              <img src="/logo.png" alt="FLSxMUN 2.0" className="w-full h-full object-contain" />
            </div>
            <div className="space-y-1">
              <span className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-ivory block">
                FLSxMUN
              </span>
              <span className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-beige block">
                2.0
              </span>
            </div>

            <p className="font-serif text-sm tracking-widest text-beige uppercase font-light pt-2">
              {SUMMIT_CONFIG.tagline}
            </p>

            <p className="text-xs text-ivory/50 font-light max-w-sm leading-relaxed">
              An uncompromised diplomatic sanctuary designed to challenge perspectives, sharpen debate and empower the next generation of youth leaders.
            </p>
          </div>

          {/* Column 2: Navigation Links */}
          <div className="md:col-span-3 space-y-3">
            <span className="text-[10px] uppercase tracking-architectural text-bronze font-bold block mb-4">
              CONFERENCE CHAMBERS
            </span>
            <ul className="space-y-2.5 text-xs text-ivory/70 tracking-widest uppercase">
              {['home', 'about', 'committees', 'matrices', 'advisors', 'registration', 'contact'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollTo(item)}
                    className="hover:text-beige transition-colors text-left focus:outline-none"
                    data-cursor-interactive
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Summit Convocation Details */}
          <div className="md:col-span-4 space-y-4">
            <span className="text-[10px] uppercase tracking-architectural text-bronze font-bold block mb-4">
              CONFERENCE CONVOCATION
            </span>

            <div className="space-y-3 text-xs">
              <div className="border-l border-bronze/40 pl-3">
                <span className="text-[9px] uppercase tracking-widest text-bronze block">
                  DATES
                </span>
                <span className="font-serif text-sm font-bold text-ivory tracking-wide">
                  {SUMMIT_CONFIG.datesFull}
                </span>
              </div>

              <div className="border-l border-bronze/40 pl-3">
                <span className="text-[9px] uppercase tracking-widest text-bronze block">
                  VENUE
                </span>
                <a
                  href={SUMMIT_CONFIG.venueMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-sm font-bold text-beige hover:text-white transition-colors tracking-wide inline-flex items-center gap-1.5"
                >
                  <span>{SUMMIT_CONFIG.venue}</span>
                  <ExternalLink className="w-3 h-3 text-bronze shrink-0" />
                </a>
              </div>

              <div className="border-l border-bronze/40 pl-3">
                <span className="text-[9px] uppercase tracking-widest text-bronze block">
                  TAGLINE
                </span>
                <span className="font-serif text-sm font-bold text-beige tracking-wide">
                  {SUMMIT_CONFIG.tagline}
                </span>
              </div>

              <div className="border-l border-bronze/40 pl-3">
                <span className="text-[9px] uppercase tracking-widest text-bronze block">
                  OFFICIAL DISPATCH
                </span>
                <span className="font-mono text-xs text-ivory/80 tracking-wide select-all">
                  {SUMMIT_CONFIG.contactEmail}
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Rights Notice */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-[11px] text-ivory/40 gap-4">
          <p>
            © {SUMMIT_CONFIG.registrationYear} FLSxMUN 2.0. All Rights Reserved.
          </p>
          <div className="flex items-center gap-6">
            <span>Model United Nations</span>
            <span className="w-1 h-1 rounded-full bg-bronze/40" />
            <span>Digital Diplomatic Institution</span>
          </div>
        </div>

      </div>
    </footer>
  );
};
